import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { getApiOrigin } from "../../utils/api";

// Template168: Menú/Plan de viaje de San Francisco
// Idéntico a guia-san-francisco/src/components/Page2.jsx
const Template168 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onNavigateToPage,
	pages = [],
	prebuiltGuideId,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const addAuthToken = (url) => {
		if (!url) return url;
		const t = localStorage.getItem("token");
		if (!t) return url;
		try {
			const u = new URL(url, window.location.origin);
			u.searchParams.set("token", t);
			return u.toString();
		} catch {
			return url;
		}
	};

	const resolveImage = (path) => {
		if (!path || typeof path !== "string") return path;
		if (path.startsWith("data:")) return path;
		if (path.startsWith("http")) return addAuthToken(path);
		if (prebuiltGuideId && path.startsWith("/images")) {
			const p = path.startsWith("/") ? path.slice(1) : path;
			return addAuthToken(
				`${getApiOrigin()}/api/prebuilt-guides/${prebuiltGuideId}/assets/${p}`,
			);
		}
		return path;
	};

	const defaultDias = [
		{
			image: "/images/sf/puerto-sf-menu.png",
			emoji: "🌉",
			titulo: "Downtown + Waterfront",
			tituloMovil: "Downtown\n+ Waterfront",
		},
		{
			image: "/images/sf/golden-gate-sf.png",
			emoji: "🗽",
			titulo: "Golden Gate + Alcatraz",
			tituloMovil: "Golden Gate\n+ Alcatraz",
		},
		{
			image: "/images/sf/painted-menu.png",
			emoji: "🏘️",
			titulo: "Barrios con personalidad",
			tituloMovil: "Barrios con\npersonalidad",
		},
	];

	const defaultExtras = [
		{ image: "/images/sf/transport.png", emoji: "🚃", titulo: "Transporte" },
		{ image: "/images/sf/airport.png", emoji: "✈️", titulo: "Aeropuertos" },
		{
			image: "/images/sf/excursiones-sf.png",
			emoji: "🚌",
			titulo: "Excursiones",
		},
		{ image: "/images/sf/security.png", emoji: "🛡️", titulo: "Seguridad" },
		{ image: "/images/sf/tips.png", emoji: "🎟️", titulo: "Consejos útiles" },
	];

	const diasTemplateMapping = [169, 172, 175];
	const extrasTemplateMapping = [177, 178, 179, 180, 181];

	const findPageByTemplateId = (templateId) => {
		return pages.find(
			(p) => p.templateId === templateId || p.data?.templateId === templateId,
		);
	};
	const getPageId = (page) => page?.id || page?.data?.id;

	const pageData = page.data || {};
	const diasRaw = pageData.dias?.length > 0 ? pageData.dias : defaultDias;
	const extrasRaw =
		pageData.extras?.length > 0 ? pageData.extras : defaultExtras;

	const dias = diasRaw.map((dia, index) => {
		const templateId = diasTemplateMapping[index];
		const targetPage = templateId ? findPageByTemplateId(templateId) : null;
		return {
			...dia,
			pageId: dia.pageId || (targetPage ? getPageId(targetPage) : null),
		};
	});

	const extras = extrasRaw
		.filter((e) => e && e.titulo !== "Kofi" && !e.image?.includes("kofi"))
		.map((extra, index) => {
			const templateId = extrasTemplateMapping[index];
			const targetPage = templateId ? findPageByTemplateId(templateId) : null;
			return {
				...extra,
				pageId: extra.pageId || (targetPage ? getPageId(targetPage) : null),
			};
		});

	const actualHeaderColor = headerBackgroundColor || "#F2B705";
	const headerText = page.texts?.[0]?.content || "Plan de viaje";
	const logoImage = resolveImage(
		page.canvasImages?.find((img) => img.id === "logo")?.imageUrl ||
			"/images/sf/logo.png",
	);

	const handleNavigate = (pageId) => {
		if (onNavigateToPage && pageId) onNavigateToPage(pageId);
	};

	const renderItem = (item, index, isDay, onClick) => (
		<Box
			key={index}
			onClick={onClick}
			sx={{
				borderRadius: "12px",
				padding: 0,
				display: "flex",
				alignItems: "center",
				gap: "10px",
				position: "relative",
				minHeight: 30,
				width: "auto",
				cursor: item.pageId ? "pointer" : "default",
				transition: "transform 0.3s ease",
				marginLeft: "20px",
				"&:hover": item.pageId
					? {
							transform: "translateY(-5px)",
							"& .box-sf": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
						}
					: {},
			}}
		>
			{item.image && (
				<Box
					component="img"
					src={resolveImage(item.image)}
					alt=""
					sx={{
						width: 45,
						zIndex: 100,
						position: "absolute",
						left: -20,
						...(!isDay && { objectFit: "contain" }),
					}}
				/>
			)}
			<Box
				className="box-sf"
				sx={{
					backgroundColor: "#fff",
					borderRadius: "12px",
					padding: "18px 16px",
					display: "flex",
					alignItems: "center",
					gap: "10px",
					position: "relative",
					minHeight: 30,
					width: "100%",
					marginLeft: 0,
					textAlign: "left",
					height: "auto",
					flex: 1,
					transition: "box-shadow 0.3s ease",
					paddingLeft: "16px",
				}}
			>
				<Box
					sx={{
						fontSize: "13px",
						fontWeight: 600,
						color: "#333",
						marginLeft: "20px",
						whiteSpace: "normal",
					}}
				>
					{isDay ? `Día ${index + 1}: ${item.titulo}` : item.titulo}
				</Box>
			</Box>
		</Box>
	);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				<Box
					component="img"
					src={logoImage}
					alt="logo"
					sx={{
						width: isSmallScreen ? 66 : 80,
						position: "absolute",
						right: 2,
						top: 18,
						zIndex: 999999,
					}}
				/>
			</Box>

			<Box data-block-id="text-1" sx={{ padding: isSmallScreen ? "10px 10px" : "20px 16px" }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						flexWrap: "nowrap",
					}}
				>
					{dias.map((dia, index) =>
						renderItem(
							dia,
							index,
							true,
							() => dia.pageId && handleNavigate(dia.pageId),
						),
					)}
				</Box>

				<Box
					component="h3"
					sx={{
						fontFamily: "'Poppins', sans-serif",
						fontSize: 12,
						fontWeight: 700,
						margin: 0,
						textTransform: "uppercase",
						marginTop: "16px",
						marginBottom: "16px",
					}}
				>
					Información útil{" "}
				</Box>

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						flexWrap: "nowrap",
					}}
				>
					{extras.map((extra, index) =>
						renderItem(
							extra,
							index,
							false,
							() => extra.pageId && handleNavigate(extra.pageId),
						),
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template168.thumbnail = "/images/template-1.png";
export default Template168;
